import { EditorView, keymap, lineNumbers, highlightActiveLineGutter, highlightSpecialChars, drawSelection, dropCursor, rectangularSelection, crosshairCursor, highlightActiveLine } from '@codemirror/view';
import { EditorState, Compartment } from '@codemirror/state';
import { defaultKeymap, history, historyKeymap, indentWithTab, toggleComment } from '@codemirror/commands';
import { indentOnInput, foldGutter, foldKeymap, bracketMatching, StreamLanguage } from '@codemirror/language';
import { closeBrackets, closeBracketsKeymap, autocompletion, completionKeymap } from '@codemirror/autocomplete';
import { searchKeymap, highlightSelectionMatches, openSearchPanel } from '@codemirror/search';
import { lintKeymap } from '@codemirror/lint';
import { stex } from '@codemirror/legacy-modes/mode/stex';
import { vim } from '@replit/codemirror-vim';
import { genisEditorTheme, genisHighlightStyle } from './theme';

export interface EditorOptions {
  initialContent?: string;
  readOnly?: boolean;
  onContentChange?: (content: string) => void;
  onCursorChange?: (line: number, col: number) => void;
  /** Extra CodeMirror extensions (e.g. y-codemirror collab). */
  extensions?: import('@codemirror/state').Extension[];
}

const readOnlyCompartment = new Compartment();
const vimCompartment = new Compartment();

export function createCodeMirrorEditor(
  container: HTMLElement,
  options: EditorOptions = {}
): EditorView {
  const latexLanguage = StreamLanguage.define(stex);

  // LaTeX-specific autocompletions
  const latexCompletions = autocompletion({
    override: [
      (context) => {
        const word = context.matchBefore(/\\[a-zA-Z]*/);
        if (!word) return null;
        if (word.from === word.to && !context.explicit) return null;

        return {
          from: word.from,
          options: [
            // Document structure
            { label: '\\documentclass', type: 'keyword', detail: 'Document class', apply: '\\documentclass{article}' },
            { label: '\\usepackage', type: 'keyword', detail: 'Import package', apply: '\\usepackage{}' },
            { label: '\\begin', type: 'keyword', detail: 'Begin environment', apply: '\\begin{}\n\n\\end{}' },
            { label: '\\end', type: 'keyword', detail: 'End environment', apply: '\\end{}' },
            // Sections
            { label: '\\section', type: 'keyword', detail: 'Section heading', apply: '\\section{}' },
            { label: '\\subsection', type: 'keyword', detail: 'Subsection heading', apply: '\\subsection{}' },
            { label: '\\subsubsection', type: 'keyword', detail: 'Subsubsection heading', apply: '\\subsubsection{}' },
            { label: '\\chapter', type: 'keyword', detail: 'Chapter heading', apply: '\\chapter{}' },
            { label: '\\paragraph', type: 'keyword', detail: 'Paragraph heading', apply: '\\paragraph{}' },
            // Formatting
            { label: '\\textbf', type: 'function', detail: 'Bold text', apply: '\\textbf{}' },
            { label: '\\textit', type: 'function', detail: 'Italic text', apply: '\\textit{}' },
            { label: '\\underline', type: 'function', detail: 'Underlined text', apply: '\\underline{}' },
            { label: '\\emph', type: 'function', detail: 'Emphasized text', apply: '\\emph{}' },
            { label: '\\texttt', type: 'function', detail: 'Monospace text', apply: '\\texttt{}' },
            // Math
            { label: '\\frac', type: 'function', detail: 'Fraction', apply: '\\frac{}{}' },
            { label: '\\sqrt', type: 'function', detail: 'Square root', apply: '\\sqrt{}' },
            { label: '\\sum', type: 'function', detail: 'Summation', apply: '\\sum_{i=1}^{n}' },
            { label: '\\int', type: 'function', detail: 'Integral', apply: '\\int_{a}^{b}' },
            { label: '\\lim', type: 'function', detail: 'Limit', apply: '\\lim_{x \\to \\infty}' },
            { label: '\\alpha', type: 'constant', detail: 'Greek alpha' },
            { label: '\\beta', type: 'constant', detail: 'Greek beta' },
            { label: '\\gamma', type: 'constant', detail: 'Greek gamma' },
            { label: '\\delta', type: 'constant', detail: 'Greek delta' },
            { label: '\\epsilon', type: 'constant', detail: 'Greek epsilon' },
            { label: '\\lambda', type: 'constant', detail: 'Greek lambda' },
            { label: '\\mu', type: 'constant', detail: 'Greek mu' },
            { label: '\\sigma', type: 'constant', detail: 'Greek sigma' },
            { label: '\\theta', type: 'constant', detail: 'Greek theta' },
            { label: '\\pi', type: 'constant', detail: 'Greek pi' },
            { label: '\\omega', type: 'constant', detail: 'Greek omega' },
            { label: '\\infty', type: 'constant', detail: 'Infinity symbol' },
            // References
            { label: '\\label', type: 'function', detail: 'Set label', apply: '\\label{}' },
            { label: '\\ref', type: 'function', detail: 'Reference', apply: '\\ref{}' },
            { label: '\\cite', type: 'function', detail: 'Citation', apply: '\\cite{}' },
            { label: '\\bibliography', type: 'function', detail: 'Bibliography', apply: '\\bibliography{}' },
            { label: '\\bibliographystyle', type: 'function', detail: 'Bibliography style', apply: '\\bibliographystyle{}' },
            // Figures & Tables
            { label: '\\includegraphics', type: 'function', detail: 'Include image', apply: '\\includegraphics[width=\\textwidth]{}' },
            { label: '\\caption', type: 'function', detail: 'Caption', apply: '\\caption{}' },
            { label: '\\hline', type: 'keyword', detail: 'Horizontal line' },
            // Lists
            { label: '\\item', type: 'keyword', detail: 'List item' },
            // Misc
            { label: '\\newcommand', type: 'keyword', detail: 'Define command', apply: '\\newcommand{\\cmd}[1]{}' },
            { label: '\\renewcommand', type: 'keyword', detail: 'Redefine command', apply: '\\renewcommand{\\cmd}[1]{}' },
            { label: '\\title', type: 'keyword', detail: 'Document title', apply: '\\title{}' },
            { label: '\\author', type: 'keyword', detail: 'Document author', apply: '\\author{}' },
            { label: '\\date', type: 'keyword', detail: 'Document date', apply: '\\date{}' },
            { label: '\\maketitle', type: 'keyword', detail: 'Render title block' },
            { label: '\\tableofcontents', type: 'keyword', detail: 'Table of contents' },
            { label: '\\newpage', type: 'keyword', detail: 'Page break' },
            { label: '\\clearpage', type: 'keyword', detail: 'Clear page' },
            { label: '\\footnote', type: 'function', detail: 'Footnote', apply: '\\footnote{}' },
            { label: '\\href', type: 'function', detail: 'Hyperlink', apply: '\\href{}{}' },
            { label: '\\url', type: 'function', detail: 'URL', apply: '\\url{}' },
          ],
        };
      },
    ],
  });

  // Auto-close \begin{env} with \end{env} on Enter
  const beginEndAutoClose = EditorView.inputHandler.of((view, from, to, text) => {
    // When user types Enter after \begin{...}
    if (text === '\n' || text === '\r\n') {
      const line = view.state.doc.lineAt(from);
      const lineText = view.state.sliceDoc(line.from, from);
      const beginMatch = lineText.match(/\\begin\{([^}]+)\}\s*$/);
      if (beginMatch) {
        const envName = beginMatch[1];
        // Check if \end{envName} already follows on the next line
        const afterCursor = view.state.sliceDoc(from, Math.min(from + 200, view.state.doc.length));
        const hasEnd = afterCursor.match(new RegExp(`^\\s*\\\\end\\{${envName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}`));
        if (!hasEnd) {
          const indent = lineText.match(/^(\s*)/)?.[1] ?? '';
          const insert = `\n${indent}  \n${indent}\\end{${envName}}`;
          view.dispatch({
            changes: { from, to, insert },
            selection: { anchor: from + 1 + indent.length + 2 }, // cursor on the blank line, indented
          });
          return true;
        }
      }
    }
    return false;
  });

  const extensions = [
    lineNumbers(),
    highlightActiveLineGutter(),
    highlightSpecialChars(),
    history(),
    foldGutter(),
    drawSelection(),
    dropCursor(),
    indentOnInput(),
    bracketMatching(),
    closeBrackets(),
    rectangularSelection(),
    crosshairCursor(),
    highlightActiveLine(),
    highlightSelectionMatches(),
    latexLanguage,
    latexCompletions,
    beginEndAutoClose,
    genisEditorTheme,
    genisHighlightStyle,
    readOnlyCompartment.of(EditorState.readOnly.of(options.readOnly ?? false)),
    vimCompartment.of([]),
    keymap.of([
      ...closeBracketsKeymap,
      ...defaultKeymap,
      ...searchKeymap,
      ...historyKeymap,
      ...foldKeymap,
      ...completionKeymap,
      ...lintKeymap,
      indentWithTab,
    ]),
    EditorView.lineWrapping,
    ...(options.extensions ?? []),
  ];

  // Add content change listener
  if (options.onContentChange) {
    extensions.push(
      EditorView.updateListener.of((update) => {
        if (update.docChanged) {
          options.onContentChange!(update.state.doc.toString());
        }
      })
    );
  }

  // Add cursor change listener
  if (options.onCursorChange) {
    extensions.push(
      EditorView.updateListener.of((update) => {
        if (update.selectionSet || update.docChanged) {
          const pos = update.state.selection.main.head;
          const line = update.state.doc.lineAt(pos);
          options.onCursorChange!(line.number, pos - line.from + 1);
        }
      })
    );
  }

  const state = EditorState.create({
    doc: options.initialContent ?? '',
    extensions,
  });

  const view = new EditorView({
    state,
    parent: container,
    root: document,
  });

  return view;
}

export function setEditorContent(view: EditorView, content: string): void {
  view.dispatch({
    changes: {
      from: 0,
      to: view.state.doc.length,
      insert: content,
    },
  });
}

export function getEditorContent(view: EditorView): string {
  return view.state.doc.toString();
}

export function setCursorPosition(view: EditorView, line: number, col: number): void {
  const lineInfo = view.state.doc.line(Math.min(line, view.state.doc.lines));
  const pos = Math.min(lineInfo.from + col - 1, lineInfo.to);
  view.dispatch({
    selection: { anchor: pos },
    scrollIntoView: true,
  });
  view.focus();
}

export function insertAtCursor(view: EditorView, text: string): void {
  const pos = view.state.selection.main.head;
  view.dispatch({
    changes: { from: pos, insert: text },
    selection: { anchor: pos + text.length },
  });
  view.focus();
}

export function wrapSelection(view: EditorView, before: string, after: string): void {
  const { from, to } = view.state.selection.main;
  const selected = view.state.sliceDoc(from, to);
  view.dispatch({
    changes: { from, to, insert: before + selected + after },
    selection: { anchor: from + before.length, head: from + before.length + selected.length },
  });
  view.focus();
}

/**
 * Scroll the editor to a specific line and briefly highlight it.
 * Used for inverse SyncTeX (PDF → source).
 */
export function scrollToLine(view: EditorView, line: number): void {
  const clampedLine = Math.min(Math.max(1, line), view.state.doc.lines);
  const lineInfo = view.state.doc.line(clampedLine);
  view.dispatch({
    selection: { anchor: lineInfo.from },
    scrollIntoView: true,
  });
  view.focus();

  // Brief highlight effect using a temporary decoration
  const dom = view.domAtPos(lineInfo.from);
  if (dom.node.parentElement) {
    const lineEl = dom.node.parentElement.closest('.cm-line');
    if (lineEl) {
      (lineEl as HTMLElement).style.backgroundColor = '#FDE68A88'; // amber highlight
      setTimeout(() => {
        (lineEl as HTMLElement).style.backgroundColor = '';
      }, 1500);
    }
  }
}

/**
 * Get the current cursor line number (1-indexed).
 */
export function getCursorLine(view: EditorView): number {
  const pos = view.state.selection.main.head;
  return view.state.doc.lineAt(pos).number;
}

/**
 * Toggle Vim keybindings on/off.
 */
export function setVimMode(view: EditorView, enabled: boolean): void {
  view.dispatch({
    effects: vimCompartment.reconfigure(enabled ? vim() : []),
  });
}

export function toggleCommentCommand(view: EditorView): void {
  toggleComment(view);
}

export function openSearchCommand(view: EditorView): void {
  openSearchPanel(view);
}
